import type { LoyaltyCard, Store } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@recipes/ui';
import { Camera, CreditCard, Eye } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { toastError } from '@/lib/errors';
import { preparePhoto } from '@/lib/images/prepare-photo';
import { createLoyaltyCardLocal, updateLoyaltyCardLocal, updateStoreLocal } from './catalog-queue';
import { useCardImage, useLoyaltyCards, useShoppingAt } from './hooks';

/**
 * Loyalty cards (user, 2026-09-25: "Payback"): a name and a picture of the card, shared with
 * the household, kept on the device so the checkout scanner can read the picture without a
 * connection. A store may point at the card shown there; the shopping list has a "P" button.
 */

const NONE = '__none__';
/** A card picture: the barcode must stay readable, but 1600 px on the long edge is plenty. */
const CARD_PHOTO_MAX_EDGE = 1600;

function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

/** The picture of a card, from the device when it is there; a card icon without one. */
export function CardImage({
  card,
  className,
  large = false,
}: {
  card: LoyaltyCard;
  className?: string;
  large?: boolean;
}) {
  const { t } = useTranslation();
  const src = useCardImage(card);
  if (!src)
    return (
      <div
        role="img"
        aria-label={t('catalog.cards.noImage')}
        className={cn(
          'flex items-center justify-center rounded-md bg-muted text-muted-foreground',
          className,
        )}
      >
        <CreditCard aria-hidden className={large ? 'size-12' : 'size-5'} />
      </div>
    );
  return (
    <img
      src={src}
      alt={t('catalog.cards.imageAlt', { name: card.name })}
      className={cn('rounded-md object-contain', className)}
      data-testid={large ? 'card-view-image' : undefined}
    />
  );
}

/** Name and picture of a card – new or existing. */
export function LoyaltyCardDialog({
  open,
  onOpenChange,
  card,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card?: LoyaltyCard | null;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(card?.name ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName(card?.name ?? '');
      setFile(null);
    }
  }, [open, card]);
  const preview = useObjectUrl(file);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const image = file
        ? { file: await preparePhoto(file, CARD_PHOTO_MAX_EDGE), name: file.name }
        : null;
      if (card) await updateLoyaltyCardLocal(card, { name: trimmed, image });
      else await createLoyaltyCardLocal({ name: trimmed, image });
      toast.success(t('catalog.cards.saved'));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="card-dialog">
        <DialogHeader>
          <DialogTitle>{card ? t('catalog.cards.edit') : t('catalog.cards.add')}</DialogTitle>
          <DialogDescription>{t('catalog.cards.hint')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void save(event)} className="space-y-4">
          <div>
            <Label htmlFor={nameId}>{t('catalog.cards.name')}</Label>
            <Input
              id={nameId}
              className="mt-1"
              value={name}
              maxLength={120}
              required
              placeholder={t('catalog.cards.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
              data-testid="card-name"
            />
          </div>
          <div className="space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              data-testid="card-photo-input"
            />
            {preview ? (
              <img src={preview} alt="" className="max-h-48 rounded-md object-contain" />
            ) : (
              card && <CardImage card={card} className="h-32 w-full" />
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              data-testid="card-photo-button"
            >
              <Camera aria-hidden />{' '}
              {file || card?.image ? t('catalog.cards.photoChange') : t('catalog.cards.photoAdd')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('catalog.cards.photoHint')}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!name.trim()} data-testid="card-save">
              {t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The card, large and on a light ground, for the scanner at the checkout. */
export function LoyaltyCardViewDialog({
  card,
  open,
  onOpenChange,
}: {
  card: LoyaltyCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('common.close')}
        fullscreenOnMobile
        className="bg-paper text-ink sm:max-w-xl"
        data-testid="card-view"
      >
        <DialogHeader>
          <DialogTitle>{card.name}</DialogTitle>
          <DialogDescription className="text-ink/70">
            {t('catalog.cards.showHint')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 items-center justify-center">
          <CardImage card={card} large className="max-h-[70vh] w-full" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The "P" in the sort row of the shopping list: in the primary colour when the store I am
 * shopping at has a card. A tap shows that card – or the only one there is – at once;
 * with several cards and no store it asks which.
 */
export function PaybackButton() {
  const { t } = useTranslation();
  const cards = useLoyaltyCards();
  const { store } = useShoppingAt();
  const [viewing, setViewing] = useState<LoyaltyCard | null>(null);
  if (cards.length === 0) return null;
  const storeCard = store ? (cards.find((c) => c.id === store.loyaltyCardId) ?? null) : null;
  const direct = storeCard ?? (cards.length === 1 ? cards[0]! : null);
  const state = storeCard ? 'assigned' : 'none';
  const label = storeCard
    ? t('catalog.cards.buttonFor', { name: storeCard.name })
    : t('catalog.cards.button');
  const button = (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={cn('h-9 w-9 shrink-0', state === 'assigned' && 'border-primary text-primary')}
      aria-label={label}
      title={label}
      onClick={direct ? () => setViewing(direct) : undefined}
      data-testid="shopping-payback-button"
      data-state-card={state}
    >
      <span aria-hidden className="text-base font-bold leading-none">
        P
      </span>
    </Button>
  );
  return (
    <>
      {direct ? (
        button
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
          <DropdownMenuContent align="start" data-testid="shopping-payback-pick">
            <DropdownMenuLabel>{t('catalog.cards.pick')}</DropdownMenuLabel>
            {cards.map((card) => (
              <DropdownMenuItem
                key={card.id}
                onSelect={() => setViewing(card)}
                data-testid="shopping-payback-option"
              >
                <CardImage card={card} className="size-6" /> {card.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {viewing && (
        <LoyaltyCardViewDialog
          card={viewing}
          open
          onOpenChange={(next) => {
            if (!next) setViewing(null);
          }}
        />
      )}
    </>
  );
}

/** On the store page: which card is shown at this store's checkout. */
export function StoreLoyaltyCardSection({ store }: { store: Store }) {
  const { t } = useTranslation();
  const cards = useLoyaltyCards();
  const ids = { heading: useId(), select: useId() };
  const [viewing, setViewing] = useState(false);
  if (cards.length === 0) return null;
  const card = cards.find((c) => c.id === store.loyaltyCardId) ?? null;

  const change = async (id: string | null) => {
    try {
      await updateStoreLocal(store, { loyaltyCardId: id });
      toast.success(t('catalog.cards.storeSaved'));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <section
      aria-labelledby={ids.heading}
      className="space-y-3 rounded-lg border p-4"
      data-testid="store-card"
    >
      <h2 id={ids.heading} className="text-lg font-semibold">
        {t('catalog.cards.storeTitle')}
      </h2>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Label htmlFor={ids.select}>{t('catalog.cards.storeLabel')}</Label>
          <Select
            value={card?.id ?? NONE}
            disabled={!store.editable}
            onValueChange={(value) => void change(value === NONE ? null : value)}
          >
            <SelectTrigger id={ids.select} className="mt-1" data-testid="store-card-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('catalog.cards.none')}</SelectItem>
              {cards.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {card && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setViewing(true)}
            data-testid="store-card-show"
          >
            <Eye aria-hidden /> {t('catalog.cards.show')}
          </Button>
        )}
      </div>
      {card && <LoyaltyCardViewDialog card={card} open={viewing} onOpenChange={setViewing} />}
    </section>
  );
}
